import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import styles from './Businesses.module.css';

const BUSINESS_TYPES = [
  { value: 'CAFE', label: 'Cafe' },
  { value: 'CLINIC', label: 'Clinic' },
  { value: 'GYM', label: 'Gym' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'WHOLESALE', label: 'Wholesale' },
  { value: 'OTHER', label: 'Other' },
];

function parseFieldErrors(errors) {
  if (!errors || typeof errors !== 'object') return {};
  const result = {};
  Object.entries(errors).forEach(([key, val]) => {
    result[key] = Array.isArray(val) ? val[0] : String(val);
  });
  return result;
}

function BusinessForm({ business, onSave, onCancel }) {
  const [name, setName] = useState(business?.name || '');
  const [type, setType] = useState(business?.business_type || 'RETAIL');
  const [financialYearStart, setFinancialYearStart] = useState(business?.financial_year_start || 7);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  function clearFieldError(field) {
    setFieldErrors(prev => ({ ...prev, [field]: '' }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      const payload = { name, business_type: type, financial_year_start: financialYearStart };
      if (business) {
        await api.put(`/businesses/${business.id}/`, payload);
      } else {
        await api.post('/businesses/', payload);
      }
      onSave();
    } catch (err) {
      const data = err.response?.data || {};
      const fe = parseFieldErrors(data.errors);
      if (Object.keys(fe).length > 0) {
        setFieldErrors(fe);
      } else {
        setError(data.message || 'Failed to save business.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <h2>{business ? 'Edit Business' : 'Add Business'}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}
          <label className={styles.label}>
            Business Name
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); clearFieldError('name'); }}
              className={`${styles.input} ${fieldErrors.name ? styles.inputError : ''}`}
              required
            />
            {fieldErrors.name && <span className={styles.fieldError}>{fieldErrors.name}</span>}
          </label>
          <label className={styles.label}>
            Business Type
            <select
              value={type}
              onChange={e => { setType(e.target.value); clearFieldError('business_type'); }}
              className={`${styles.input} ${fieldErrors.business_type ? styles.inputError : ''}`}
            >
              {BUSINESS_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {fieldErrors.business_type && <span className={styles.fieldError}>{fieldErrors.business_type}</span>}
          </label>
          <label className={styles.label}>
            Financial Year Start (Month)
            <select
              value={financialYearStart}
              onChange={e => { setFinancialYearStart(Number(e.target.value)); clearFieldError('financial_year_start'); }}
              className={`${styles.input} ${fieldErrors.financial_year_start ? styles.inputError : ''}`}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
            {fieldErrors.financial_year_start && <span className={styles.fieldError}>{fieldErrors.financial_year_start}</span>}
          </label>
          <div className={styles.formActions}>
            <button type="button" onClick={onCancel} className={styles.btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={styles.btnPrimary}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Field editor (shared by create and edit flows) ─────────────────────────────
function FieldEditor({ fields, onChange }) {
  function updateField(index, key, value) {
    const next = [...fields];
    next[index] = { ...next[index], [key]: value };
    onChange(next);
  }
  function addField() {
    onChange([...fields, { name: '', type: 'number' }]);
  }
  function removeField(index) {
    if (fields.length > 1) onChange(fields.filter((_, i) => i !== index));
  }

  return (
    <div className={styles.fieldEditor}>
      <div className={styles.fieldEditorHeader}>
        <span className={styles.fieldEditorCol}>Field Name</span>
        <span className={styles.fieldEditorColFixed}>Type</span>
        <span className={styles.fieldEditorColDel} />
      </div>
      {fields.map((f, i) => (
        <div key={i} className={styles.fieldEditorRow}>
          <input
            type="text"
            value={f.name}
            onChange={e => updateField(i, 'name', e.target.value)}
            placeholder="e.g. Revenue"
            className={`${styles.input} ${styles.fieldNameInput}`}
            autoFocus={i === fields.length - 1 && fields.length > 1}
          />
          <select
            value={f.type}
            onChange={e => updateField(i, 'type', e.target.value)}
            className={`${styles.input} ${styles.fieldTypeSelect}`}
          >
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="text">Text</option>
          </select>
          <button
            type="button"
            onClick={() => removeField(i)}
            disabled={fields.length === 1}
            className={styles.removeFieldBtn}
            title="Remove field"
          >
            &times;
          </button>
        </div>
      ))}
      <button type="button" onClick={addField} className={styles.btnAddField}>
        + Add Field
      </button>
    </div>
  );
}

// ── Grid template helpers ─────────────────────────────────────────────────────

function isGridTemplate(fields) {
  return fields && typeof fields === 'object' && !Array.isArray(fields) && fields.type === 'grid';
}

function makeColId() { return `c${Date.now()}${Math.floor(Math.random() * 1000)}`; }
function makeRowId() { return `r${Date.now()}${Math.floor(Math.random() * 1000)}`; }

function makeDefaultGrid() {
  return {
    type: 'grid',
    columns: [{ id: makeColId(), label: '' }],
    rows: [{ id: makeRowId(), label: '', type: 'number' }],
    computed_columns: [{ id: 'total', label: 'TOTAL', formula: 'sum' }],
  };
}

// ── Modality column picker ────────────────────────────────────────────────────
// Drop-down of existing modalities + inline "Add new item" option.
function ModalityColumnPicker({ modalities, value, onChange, onModalityAdded }) {
  const [addingNew, setAddingNew] = useState(false);
  const [newName,   setNewName]   = useState('');
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState('');

  async function saveNew() {
    const name = newName.trim();
    if (!name) return;
    setSaving(true); setErr('');
    try {
      const created = await onModalityAdded(name);
      onChange(created.name);
      setAddingNew(false);
      setNewName('');
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to add item.');
    } finally { setSaving(false); }
  }

  if (addingNew) {
    return (
      <div className={styles.modalityInlineAdd}>
        <input
          autoFocus
          type="text"
          value={newName}
          onChange={e => { setNewName(e.target.value); setErr(''); }}
          onKeyDown={e => { if (e.key === 'Enter') saveNew(); if (e.key === 'Escape') setAddingNew(false); }}
          className={styles.modalityInlineInput}
          placeholder="New item name"
        />
        <button type="button" onClick={saveNew} disabled={saving || !newName.trim()} className={styles.modalityInlineSave}>
          {saving ? '…' : '✓'}
        </button>
        <button type="button" onClick={() => { setAddingNew(false); setErr(''); }} className={styles.modalityInlineCancel}>✕</button>
        {err && <span className={styles.modalityInlineErr}>{err}</span>}
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={e => {
        if (e.target.value === '__add__') { setAddingNew(true); }
        else { onChange(e.target.value); }
      }}
      className={styles.gridColChipInput}
    >
      <option value="">— select item —</option>
      {modalities.map(m => (
        <option key={m.id} value={m.name}>{m.name}</option>
      ))}
      <option disabled>──────────────</option>
      <option value="__add__">＋ Add new item…</option>
    </select>
  );
}

// ── Modality manager (inside business card) ───────────────────────────────────
function ModalityManager({ businessId }) {
  const [open,       setOpen]       = useState(false);
  const [modalities, setModalities] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [newName,    setNewName]    = useState('');
  const [adding,     setAdding]     = useState(false);
  const [err,        setErr]        = useState('');

  const fetchModalities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/businesses/${businessId}/modalities/`);
      if (res.data.success) setModalities(res.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [businessId]);

  useEffect(() => { if (open) fetchModalities(); }, [open, fetchModalities]);

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    setAdding(true); setErr('');
    try {
      const res = await api.post(`/businesses/${businessId}/modalities/`, { name });
      if (res.data.success) {
        setModalities(prev => [...prev, res.data.data]);
        setNewName('');
      }
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to add item.');
    } finally { setAdding(false); }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/businesses/${businessId}/modalities/${id}/`);
      setModalities(prev => prev.filter(m => m.id !== id));
    } catch { /* ignore */ }
  }

  return (
    <div className={styles.modalitySection}>
      <button
        type="button"
        className={styles.modalitySectionToggle}
        onClick={() => setOpen(v => !v)}
      >
        <span>Business Items / Modalities</span>
        <span className={`${styles.modalityChevron} ${open ? styles.modalityChevronOpen : ''}`}>▾</span>
      </button>

      {open && (
        <div className={styles.modalityBody}>
          {loading ? (
            <p className={styles.modalityLoading}>Loading…</p>
          ) : (
            <>
              <div className={styles.modalityChips}>
                {modalities.length === 0 && (
                  <span className={styles.modalityEmpty}>No items yet. Add your first one below.</span>
                )}
                {modalities.map(m => (
                  <span key={m.id} className={styles.modalityChip}>
                    {m.name}
                    <button
                      type="button"
                      className={styles.modalityChipDel}
                      onClick={() => handleDelete(m.id)}
                      title="Remove"
                    >×</button>
                  </span>
                ))}
              </div>
              <div className={styles.modalityAddRow}>
                <input
                  type="text"
                  value={newName}
                  onChange={e => { setNewName(e.target.value); setErr(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                  className={styles.modalityAddInput}
                  placeholder="e.g. MRI, CT, Café Latte…"
                />
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={adding || !newName.trim()}
                  className={styles.modalityAddBtn}
                >
                  {adding ? 'Adding…' : 'Add'}
                </button>
              </div>
              {err && <p className={styles.modalityErr}>{err}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GridTemplateEditor({ grid, onChange, modalities = [], onModalityAdded }) {
  const { columns = [], rows = [], computed_columns = [] } = grid;
  const hasTotal = computed_columns.some(cc => cc.formula === 'sum');

  function setColumns(cols) { onChange({ ...grid, columns: cols }); }
  function setRows(rs) { onChange({ ...grid, rows: rs }); }

  // Column ops
  function addCol() {
    setColumns([...columns, { id: makeColId(), label: '' }]);
  }
  function removeCol(id) {
    if (columns.length <= 1) return;
    setColumns(columns.filter(c => c.id !== id));
  }
  function updateColLabel(id, label) {
    setColumns(columns.map(c => c.id === id ? { ...c, label } : c));
  }

  // Row ops
  function addRow() {
    setRows([...rows, { id: makeRowId(), label: '', type: 'number' }]);
  }
  function removeRow(id) {
    if (rows.length <= 1) return;
    setRows(rows.filter(r => r.id !== id));
  }
  function updateRow(id, key, val) {
    setRows(rows.map(r => r.id === id ? { ...r, [key]: val } : r));
  }

  // Toggle TOTAL computed column
  function toggleTotal() {
    const without = computed_columns.filter(cc => cc.formula !== 'sum');
    onChange({
      ...grid,
      computed_columns: hasTotal
        ? without
        : [...without, { id: 'total', label: 'TOTAL', formula: 'sum' }],
    });
  }

  return (
    <div className={styles.gridEditor}>
      {/* Columns */}
      <div className={styles.gridSection}>
        <div className={styles.gridSectionHead}>
          <span className={styles.gridSectionTitle}>Columns</span>
          <span className={styles.gridSectionHint}>e.g. CT, MRI, US INJ</span>
        </div>
        <div className={styles.gridColsList}>
          {columns.map(col => (
            <div key={col.id} className={styles.gridColChip}>
              <ModalityColumnPicker
                modalities={modalities}
                value={col.label}
                onChange={label => updateColLabel(col.id, label)}
                onModalityAdded={onModalityAdded}
              />
              {columns.length > 1 && (
                <button type="button" onClick={() => removeCol(col.id)} className={styles.gridChipDel} title="Remove">×</button>
              )}
            </div>
          ))}
          <button type="button" onClick={addCol} className={styles.gridAddBtn}>+ Add Column</button>
        </div>
      </div>

      {/* Rows */}
      <div className={styles.gridSection}>
        <div className={styles.gridSectionHead}>
          <span className={styles.gridSectionTitle}>Rows</span>
          <span className={styles.gridSectionHint}>e.g. Projected Volume, Revenue</span>
        </div>
        <div className={styles.gridRowsList}>
          {rows.map(row => (
            <div key={row.id} className={styles.gridRowItem}>
              <input
                type="text"
                value={row.label}
                onChange={e => updateRow(row.id, 'label', e.target.value)}
                className={styles.gridRowLabelInput}
                placeholder="Row name"
              />
              <select
                value={row.type}
                onChange={e => updateRow(row.id, 'type', e.target.value)}
                className={styles.gridRowTypeSelect}
              >
                <option value="number">Number</option>
                <option value="currency">Currency ($)</option>
                <option value="text">Text</option>
              </select>
              {rows.length > 1 && (
                <button type="button" onClick={() => removeRow(row.id)} className={styles.gridChipDel} title="Remove row">×</button>
              )}
            </div>
          ))}
          <button type="button" onClick={addRow} className={styles.gridAddBtn}>+ Add Row</button>
        </div>
      </div>

      {/* Computed columns */}
      <div className={styles.gridSection}>
        <span className={styles.gridSectionTitle}>Computed Columns</span>
        <label className={styles.gridComputedToggle}>
          <input type="checkbox" checked={hasTotal} onChange={toggleTotal} />
          <span>TOTAL — auto-sum all data columns per row</span>
        </label>
      </div>

      {/* Preview */}
      <div className={styles.gridSection}>
        <span className={styles.gridSectionTitle}>Preview</span>
        <div className={styles.gridPreviewWrap}>
          <table className={styles.gridPreviewTable}>
            <thead>
              <tr>
                <th className={styles.gridPreviewCorner}>Date</th>
                {columns.map(col => (
                  <th key={col.id} className={styles.gridPreviewColHead}>
                    {col.label || <span className={styles.gridPlaceholderText}>(unnamed)</span>}
                  </th>
                ))}
                {hasTotal && <th className={styles.gridPreviewTotalHead}>TOTAL</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td className={styles.gridPreviewRowLabel}>
                    {row.label || <span className={styles.gridPlaceholderText}>(unnamed)</span>}
                  </td>
                  {columns.map(col => (
                    <td key={col.id} className={styles.gridPreviewCell}>
                      {row.type === 'currency' ? '$0.00' : row.type === 'number' ? '0' : '—'}
                    </td>
                  ))}
                  {hasTotal && (
                    <td className={styles.gridPreviewTotalCell}>
                      {row.type === 'currency' ? '$0.00' : '0'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Template card ─────────────────────────────────────────────────────────────
function TemplateCard({ template: schema, businessId, onSaved, modalities = [], onModalityAdded }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editFields, setEditFields] = useState([]);
  const [editGrid, setEditGrid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');

  const fields = schema.fields_decoded || [];
  const isGrid = isGridTemplate(fields);

  function startEdit() {
    setEditName(schema.name || '');
    if (isGrid) {
      setEditGrid(JSON.parse(JSON.stringify(fields))); // deep copy
      setEditFields([]);
    } else {
      setEditFields(Array.isArray(fields) ? fields.map(f => ({ ...f })) : []);
      setEditGrid(null);
    }
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function saveEdit() {
    if (!editName.trim()) {
      setError('Template name is required.');
      return;
    }
    if (isGrid || editGrid) {
      const g = editGrid;
      if (!g || !g.columns?.length || !g.rows?.length) {
        setError('Grid template must have at least one column and one row.');
        return;
      }
      if (g.columns.some(c => !c.label.trim())) {
        setError('All columns must have a label.');
        return;
      }
      if (g.rows.some(r => !r.label.trim())) {
        setError('All rows must have a label.');
        return;
      }
      setSaving(true);
      setError('');
      try {
        await api.patch(`/businesses/${businessId}/templates/${schema.id}/`, {
          name: editName.trim(),
          fields: editGrid,
        });
        setEditing(false);
        onSaved();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save template.');
      } finally {
        setSaving(false);
      }
    } else {
      if (editFields.some(f => !f.name.trim())) {
        setError('All fields must have a name.');
        return;
      }
      setSaving(true);
      setError('');
      try {
        await api.patch(`/businesses/${businessId}/templates/${schema.id}/`, {
          name: editName.trim(),
          fields: editFields,
        });
        setEditing(false);
        onSaved();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save template.');
      } finally {
        setSaving(false);
      }
    }
  }

  async function toggleActive() {
    setToggling(true);
    setError('');
    try {
      await api.patch(`/businesses/${businessId}/templates/${schema.id}/`, {
        is_active: !schema.is_active,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update template.');
      setToggling(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete template "${schema.name || `Version ${schema.version}`}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/businesses/${businessId}/templates/${schema.id}/`);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete template.');
      setDeleting(false);
    }
  }

  return (
    <div className={`${styles.templateCard} ${schema.is_active ? styles.templateCardActive : ''}`}>
      {/* Card header */}
      <div className={styles.templateCardHeader}>
        <div className={styles.templateCardMeta}>
          <span className={styles.templateName}>{schema.name || `Version ${schema.version}`}</span>
          <span className={styles.templateVersion}>v{schema.version}</span>
          <span className={`${styles.templateBadge} ${schema.is_active ? styles.active : ''}`}>
            {schema.is_active ? 'Active' : 'Inactive'}
          </span>
          <span className={styles.entriesCount}>
            {schema.entries_count === 0
              ? 'No entries yet'
              : `${schema.entries_count} ${schema.entries_count === 1 ? 'entry' : 'entries'}`}
          </span>
        </div>

        {!editing && (
          <div className={styles.cardActionBtns}>
            {/* Edit */}
            <button
              onClick={startEdit}
              className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
              title="Edit"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>

            {/* Disable / Enable */}
            {schema.is_active ? (
              <button
                onClick={toggleActive}
                disabled={toggling}
                className={`${styles.iconBtn} ${styles.iconBtnDeactivate}`}
                title="Disable"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1"/>
                  <rect x="14" y="4" width="4" height="16" rx="1"/>
                </svg>
              </button>
            ) : (
              <button
                onClick={toggleActive}
                disabled={toggling}
                className={`${styles.iconBtn} ${styles.iconBtnActivate}`}
                title="Enable"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21"/>
                </svg>
              </button>
            )}

            {/* Delete */}
            <button
              onClick={handleDelete}
              disabled={deleting}
              className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
              title="Delete"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
                <path d="M9 6V4h6v2"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {error && !editing && <div className={styles.cardError}>{error}</div>}

      {/* Editing mode */}
      {editing ? (
        <div className={styles.templateEditBody}>
          {error && <div className={styles.error}>{error}</div>}
          <label className={styles.templateNameLabel}>
            Template Name
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              className={styles.input}
              placeholder="e.g. Daily Radiology Report"
            />
          </label>
          {(isGrid || editGrid) ? (
            editGrid && <GridTemplateEditor grid={editGrid} onChange={setEditGrid} modalities={modalities} onModalityAdded={onModalityAdded} />
          ) : (
            <FieldEditor fields={editFields} onChange={setEditFields} />
          )}
          <div className={styles.templateEditActions}>
            <button type="button" onClick={cancelEdit} className={styles.btnSecondary}>Cancel</button>
            <button type="button" onClick={saveEdit} disabled={saving} className={styles.btnPrimary}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : (
        /* Read mode */
        isGrid ? (
          <div className={styles.gridReadView}>
            <div className={styles.gridReadWrap}>
              <table className={styles.gridPreviewTable}>
                <thead>
                  <tr>
                    <th className={styles.gridPreviewCorner}>Date</th>
                    {fields.columns.map(col => (
                      <th key={col.id} className={styles.gridPreviewColHead}>{col.label}</th>
                    ))}
                    {fields.computed_columns?.some(cc => cc.formula === 'sum') && (
                      <th className={styles.gridPreviewTotalHead}>TOTAL</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {fields.rows.map(row => (
                    <tr key={row.id}>
                      <td className={styles.gridPreviewRowLabel}>{row.label}</td>
                      {fields.columns.map(col => (
                        <td key={col.id} className={styles.gridPreviewCell}>
                          {row.type === 'currency' ? '$0.00' : row.type === 'number' ? '0' : '—'}
                        </td>
                      ))}
                      {fields.computed_columns?.some(cc => cc.formula === 'sum') && (
                        <td className={styles.gridPreviewTotalCell}>
                          {row.type === 'currency' ? '$0.00' : '0'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <table className={styles.fieldsTable}>
            <thead>
              <tr>
                <th className={styles.fieldsThNum}>#</th>
                <th className={styles.fieldsTh}>Field Name</th>
                <th className={styles.fieldsTh}>Type</th>
              </tr>
            </thead>
            <tbody>
              {(!Array.isArray(fields) || fields.length === 0) ? (
                <tr><td colSpan={3} className={styles.fieldsEmpty}>No fields defined.</td></tr>
              ) : (
                fields.map((f, i) => (
                  <tr key={i}>
                    <td className={styles.fieldsTdNum}>{i + 1}</td>
                    <td className={styles.fieldsTd}>{f.label || f.name}</td>
                    <td className={styles.fieldsTdType}>{f.type}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

// ── Revenue Targets panel ─────────────────────────────────────────────────────
function RevenueTargets({ business, onSaved }) {
  const [open, setOpen] = useState(false);
  const [hourly, setHourly] = useState(business.hourly_target || '');
  const [daily, setDaily] = useState(business.daily_target || '');
  const [weekly, setWeekly] = useState(business.weekly_target || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auto-fill derived fields when one changes
  function handleHourlyChange(val) {
    setHourly(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) {
      const d = (n * 8).toFixed(2);
      const w = (n * 8 * 7).toFixed(2);
      setDaily(d);
      setWeekly(w);
    }
  }

  function handleDailyChange(val) {
    setDaily(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) {
      const w = (n * 7).toFixed(2);
      setWeekly(w);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.patch(`/businesses/${business.id}/`, {
        hourly_target: hourly,
        daily_target: daily,
        weekly_target: weekly,
      });
      setSuccess('Targets saved.');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save targets.');
    } finally {
      setSaving(false);
    }
  }

  const hasTargets = business.hourly_target || business.daily_target || business.weekly_target;

  return (
    <div className={styles.targetsPanel}>
      <button
        type="button"
        className={styles.targetsPanelToggle}
        onClick={() => setOpen(o => !o)}
      >
        <span>Revenue Targets {hasTargets ? '(set)' : '(not set)'}</span>
        <span className={styles.targetsPanelCaret}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className={styles.targetsPanelBody}>
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.successMsg}>{success}</div>}
          <label className={styles.label}>
            Hourly Target
            <input
              type="number"
              min="0"
              step="0.01"
              value={hourly}
              onChange={e => handleHourlyChange(e.target.value)}
              className={styles.input}
              placeholder="e.g. 125.00"
            />
          </label>
          <label className={styles.label}>
            Daily Target
            <input
              type="number"
              min="0"
              step="0.01"
              value={daily}
              onChange={e => handleDailyChange(e.target.value)}
              className={styles.input}
              placeholder="auto: hourly × 8"
            />
          </label>
          <label className={styles.label}>
            Weekly Target
            <input
              type="number"
              min="0"
              step="0.01"
              value={weekly}
              onChange={e => setWeekly(e.target.value)}
              className={styles.input}
              placeholder="auto: daily × 7"
            />
          </label>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={styles.btnPrimary}
          >
            {saving ? 'Saving…' : 'Save Targets'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Template manager modal ────────────────────────────────────────────────────
function TemplateManager({ businessId, businessName, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLayoutType, setNewLayoutType] = useState('grid'); // 'grid' | 'flat'
  const [newFields, setNewFields] = useState([{ name: '', type: 'number' }]);
  const [newGrid, setNewGrid] = useState(makeDefaultGrid);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalities, setModalities] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get(`/businesses/${businessId}/templates/`);
      if (res.data.success) setTemplates(res.data.data || []);
    } catch { /* empty */ }
    setLoading(false);
  }, [businessId]);

  const fetchModalities = useCallback(async () => {
    try {
      const res = await api.get(`/businesses/${businessId}/modalities/`);
      if (res.data.success) setModalities(res.data.data || []);
    } catch { /* empty */ }
  }, [businessId]);

  useEffect(() => { fetchTemplates(); fetchModalities(); }, [fetchTemplates, fetchModalities]);

  async function handleModalityAdded(name) {
    const res = await api.post(`/businesses/${businessId}/modalities/`, { name });
    if (res.data.success) {
      const created = res.data.data;
      setModalities(prev => [...prev, created]);
      return created;
    }
    throw new Error('Failed to add item.');
  }

  async function handleCreate() {
    if (!newName.trim()) {
      setError('Template name is required.');
      return;
    }

    let fieldsPayload;
    if (newLayoutType === 'grid') {
      const g = newGrid;
      if (!g.columns?.length || !g.rows?.length) {
        setError('Grid template must have at least one column and one row.');
        return;
      }
      if (g.columns.some(c => !c.label.trim())) {
        setError('All column labels are required.');
        return;
      }
      if (g.rows.some(r => !r.label.trim())) {
        setError('All row labels are required.');
        return;
      }
      fieldsPayload = g;
    } else {
      if (newFields.some(f => !f.name.trim())) {
        setError('All fields must have a name.');
        return;
      }
      fieldsPayload = newFields;
    }

    setSaving(true);
    setError('');
    try {
      await api.post(`/businesses/${businessId}/templates/`, {
        name: newName.trim(),
        fields: fieldsPayload,
      });
      setShowAdd(false);
      setNewName('');
      setNewLayoutType('grid');
      setNewFields([{ name: '', type: 'number' }]);
      setNewGrid(makeDefaultGrid());
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create report template.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent} style={{ maxWidth: 680 }}>
        <div className={styles.modalHeader}>
          <div>
            <h2 style={{ margin: 0 }}>Report Templates</h2>
            {businessName && (
              <p className={styles.modalSubtitle}>{businessName}</p>
            )}
          </div>
          <button onClick={onClose} className={styles.closeBtn}>&times;</button>
        </div>

        {loading ? (
          <p className={styles.emptyText}>Loading…</p>
        ) : (
          <>
            {templates.length === 0 && !showAdd && (
              <p className={styles.emptyText}>
                No report templates yet. Create one to start collecting entries.
              </p>
            )}

            <div className={styles.templateList}>
              {templates.map(t => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  businessId={businessId}
                  onSaved={fetchTemplates}
                  modalities={modalities}
                  onModalityAdded={handleModalityAdded}
                />
              ))}
            </div>

            {showAdd ? (
              <div className={styles.addSchema}>
                <h3>New Template</h3>
                {error && <div className={styles.error}>{error}</div>}

                <label className={styles.templateNameLabel}>
                  Template Name
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    className={styles.input}
                    placeholder="e.g. Daily Radiology Report"
                    autoFocus
                  />
                </label>

                {/* Layout type toggle */}
                <div className={styles.layoutToggle}>
                  <button
                    type="button"
                    onClick={() => setNewLayoutType('grid')}
                    className={`${styles.layoutToggleBtn} ${newLayoutType === 'grid' ? styles.layoutToggleBtnActive : ''}`}
                  >
                    Grid (Spreadsheet)
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewLayoutType('flat')}
                    className={`${styles.layoutToggleBtn} ${newLayoutType === 'flat' ? styles.layoutToggleBtnActive : ''}`}
                  >
                    Simple Fields
                  </button>
                </div>

                {newLayoutType === 'grid' ? (
                  <GridTemplateEditor grid={newGrid} onChange={setNewGrid} modalities={modalities} onModalityAdded={handleModalityAdded} />
                ) : (
                  <>
                    <FieldEditor fields={newFields} onChange={setNewFields} />
                    {newFields.some(f => (f.name || '').trim()) && (
                      <div className={styles.gridSection}>
                        <span className={styles.gridSectionTitle}>Preview</span>
                        <div className={styles.simplePreviewWrap}>
                          <table className={styles.simplePreviewTable}>
                            <thead>
                              <tr>
                                {newFields.map((f, i) => (
                                  <th key={i} className={styles.simplePreviewHead}>
                                    {(f.name || '').trim() || <span className={styles.gridPlaceholderText}>(unnamed)</span>}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                {newFields.map((f, i) => (
                                  <td key={i} className={styles.simplePreviewCell}>
                                    {f.type === 'currency' ? '$0.00' : f.type === 'number' ? '0' : '—'}
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => { setShowAdd(false); setNewName(''); setError(''); }}
                    className={styles.btnSecondary}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={saving}
                    className={styles.btnPrimary}
                  >
                    {saving ? 'Creating…' : 'Create Template'}
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.templateFooter}>
                <button
                  onClick={() => { setShowAdd(true); setError(''); }}
                  className={styles.btnSecondary}
                >
                  + New Template Version
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editBusiness, setEditBusiness] = useState(null);
  const [templateBusiness, setTemplateBusiness] = useState(null);

  const fetchBusinesses = useCallback(async () => {
    try {
      const res = await api.get('/businesses/');
      if (res.data.success) setBusinesses(res.data.data || []);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  function handleAdd() {
    setEditBusiness(null);
    setShowForm(true);
  }

  function handleEdit(business) {
    setEditBusiness(business);
    setShowForm(true);
  }

  async function handleDelete(business) {
    if (!window.confirm(`Delete "${business.name}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/businesses/${business.id}/`);
      fetchBusinesses();
    } catch { /* empty */ }
  }

  function handleSaved() {
    setShowForm(false);
    setEditBusiness(null);
    fetchBusinesses();
  }

  return (
    <div>
      <PageHeader
        title="Businesses"
        subtitle="Manage your businesses and report templates"
        actions={
          <button onClick={handleAdd} className={styles.btnPrimary}>+ Add Business</button>
        }
      />

      {loading ? (
        <p className={styles.loadingText}>Loading...</p>
      ) : businesses.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No businesses yet</h3>
          <p>Create your first business to start tracking revenue.</p>
          <button onClick={handleAdd} className={styles.btnPrimary}>+ Add Business</button>
        </div>
      ) : (
        <div className={styles.grid}>
          {businesses.map(b => (
            <div key={b.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <h3>{b.name}</h3>
                <span className={styles.typeBadge}>{b.business_type}</span>
              </div>
              <div className={styles.cardMeta}>
                <span>Financial year: Month {b.financial_year_start}</span>
              </div>
              <RevenueTargets business={b} onSaved={fetchBusinesses} />
              <ModalityManager businessId={b.id} />
              <div className={styles.cardActions}>
                <button onClick={() => setTemplateBusiness(b)} className={styles.btnSecondary}>
                  Templates
                </button>
                <button onClick={() => handleEdit(b)} className={styles.btnSecondary}>Edit</button>
                <button onClick={() => handleDelete(b)} className={styles.btnDanger}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <BusinessForm
          business={editBusiness}
          onSave={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {templateBusiness && (
        <TemplateManager
          businessId={templateBusiness.id}
          businessName={templateBusiness.name}
          onClose={() => setTemplateBusiness(null)}
        />
      )}
    </div>
  );
}

export default Businesses;
